/**
 * ActionPanel - Approve / reject decision panel for the selected document.
 */
import { useEffect, useState } from 'react';
import { ActionPanelProps } from './types';

// r2b11[1]+[2] (ronda 4): el borrador del panel sube al padre (por ref) para
// que el atajo Enter apruebe CON estos valores y sepa si el formulario de
// rechazo está abierto. Se declara AQUÍ (extendiendo ActionPanelProps) y no
// en types.ts para no tocarlo en esta ronda.
export function ActionPanel({
  document,
  onApprove,
  onReject,
  isProcessing,
  onDraftChange,
}: ActionPanelProps & {
  onDraftChange?: (draft: { expiresAt: string; notes: string; rejectOpen: boolean }) => void;
}) {
  const [expiresAt, setExpiresAt] = useState(document.expiry_date || '');
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // El borrador, siempre al día en el padre: caducidad, notas y si el rechazo
  // está abierto. Se re-envía al cambiar de documento (el efecto de reset de
  // abajo dispara los setState y éstos, este push).
  useEffect(() => {
    onDraftChange?.({
      expiresAt,
      notes,
      rejectOpen: showRejectForm,
    });
  }, [expiresAt, notes, showRejectForm, document.id, onDraftChange]);

  // El hint prometía «Esc rechazar» y ningún handler existía: pulsar Esc no
  // hacía nada (ronda 3, r2b11[8]). Esc ABRE el formulario de rechazo —el
  // motivo es obligatorio, no se rechaza documentación PRL a ciegas— y con el
  // formulario abierto lo cierra sin tocar nada.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || isProcessing) return;
      e.preventDefault();
      setShowRejectForm((open) => !open);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isProcessing]);

  // El panel no se desmonta al pasar al siguiente documento de la cola: sólo le
  // cambia el `document`. Sin esto, la fecha de caducidad, las notas y el motivo
  // de rechazo del anterior se quedaban escritos y se aprobaba el siguiente con
  // la caducidad de otro — sobre documentación de PRL, que es lo que decide si
  // alguien puede entrar a una obra.
  useEffect(() => {
    setExpiresAt(document.expiry_date || '');
    setNotes('');
    setRejectReason('');
    setShowRejectForm(false);
  }, [document.id]);

  const handleApprove = () => {
    onApprove(expiresAt || undefined, notes || undefined);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      alert('Indica el motivo del rechazo');
      return;
    }
    onReject(rejectReason);
  };

  // Common rejection reasons
  const quickRejectReasons = [
    'Foto borrosa o ilegible',
    'Documento caducado',
    'Documento incompleto',
    'No coincide con el tipo solicitado',
  ];

  return (
    <div className="bg-white rounded-lg border border-line p-4 space-y-4">
      {/* Document info */}
      <div className="border-b border-line pb-4">
        <h3 className="font-semibold text-ink">{document.document_type}</h3>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-sm text-ink-2">{document.user_name}</span>
          {document.is_blocking && (
            <span className="px-2 py-0.5 text-xs bg-danger-soft text-danger rounded">
              Bloqueante
            </span>
          )}
        </div>
      </div>

      {!showRejectForm ? (
        <>
          {/* Expiry date */}
          <div>
            <label className="block text-sm font-medium text-ink-2 mb-1">
              Fecha de caducidad
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-ink-3 mt-1">
              Introduce la fecha visible en el documento
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-ink-2 mb-1">
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Notas adicionales..."
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleApprove}
              disabled={isProcessing}
              className={`
                flex-1 py-3 bg-brand text-white rounded-lg font-semibold text-lg
                hover:bg-brand-2 transition-colors flex items-center justify-center gap-2
                ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <span>✓</span> VALIDAR
            </button>
            <button
              onClick={() => setShowRejectForm(true)}
              disabled={isProcessing}
              className={`
                flex-1 py-3 bg-danger text-white rounded-lg font-semibold text-lg
                 transition-colors flex items-center justify-center gap-2
                ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <span>✕</span> RECHAZAR
            </button>
          </div>

          {/* Keyboard shortcuts hint */}
          <p className="text-center text-xs text-ink-3">
            Atajos: <kbd className="px-1 py-0.5 bg-surface-2 rounded">Enter</kbd> aprobar,{' '}
            <kbd className="px-1 py-0.5 bg-surface-2 rounded">Esc</kbd> rechazar (pide motivo)
          </p>
        </>
      ) : (
        <>
          {/* Reject form */}
          <div>
            <label className="block text-sm font-medium text-ink-2 mb-1">
              Motivo del rechazo
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
              placeholder="Explica por que se rechaza el documento..."
              autoFocus
            />
          </div>

          {/* Quick reasons */}
          <div className="flex flex-wrap gap-2">
            {quickRejectReasons.map((reason) => (
              <button
                key={reason}
                onClick={() => setRejectReason(reason)}
                className="px-3 py-1 text-sm bg-surface-2 text-ink-2 rounded-full hover:bg-surface-3 transition-colors"
              >
                {reason}
              </button>
            ))}
          </div>

          {/* Reject actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowRejectForm(false)}
              className="flex-1 py-2 text-ink-2 rounded-lg hover:bg-surface-2 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleReject}
              disabled={!rejectReason.trim() || isProcessing}
              className={`
                flex-1 py-2 bg-danger text-white rounded-lg font-medium
                 transition-colors
                ${(!rejectReason.trim() || isProcessing) ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              Confirmar rechazo
            </button>
          </div>
        </>
      )}
    </div>
  );
}
